import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../auth/api';
import { API_BASE } from '../../config';
import { useAuth } from '../../auth/useAuth';
import { FileText, Package, Users, BookOpen, Clock } from 'lucide-react';
import StatusBadge from '../../components/wiki/StatusBadge';
import { formatDistanceToNow } from 'date-fns';

const STAT_CARDS = [
    { icon: BookOpen, label: 'Total Entries', key: 'total_entries', color: 'var(--gold)' },
    { icon: FileText, label: 'Drafts', key: 'drafts', color: '#94a3b8' },
    { icon: Package, label: 'Categories', key: 'categories', color: 'var(--success)' },
    { icon: Users, label: 'Vendors', key: 'vendors', color: '#a78bfa' },
];

export default function WikiDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState({});
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/wiki/dashboard')
            .then((res) => {
                setStats(res.data.stats || res.data);
                setRecent(res.data.recently_edited || res.data.recent || res.data.recent_entries || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="loading"><div className="spinner" />Loading dashboard...</div>;
    }

    return (
        <div>
            {/* Welcome */}
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ color: 'var(--gold)', fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                    Welcome back, {user?.full_name || user?.fullname || user?.email || 'User'}
                </h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    Product Intelligence Dashboard
                </p>
            </div>

            {/* Stat cards */}
            <div className="stats-grid">
                {STAT_CARDS.map((stat) => (
                    <div className="stat-card" key={stat.label}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem' }}>
                            <stat.icon size={20} color={stat.color} />
                        </div>
                        <div className="stat-value" style={{ color: stat.color }}>
                            {stats[stat.key] ?? 0}
                        </div>
                        <div className="stat-label">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Recent entries */}
            <div className="card">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Clock size={16} />
                    Recently Edited
                </h3>
                {recent.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '2rem' }}>
                        No entries yet. Upload a document to get started.
                    </p>
                ) : (
                    <div className="cases-list">
                        {recent.slice(0, 10).map((entry) => (
                            <Link
                                key={entry.id}
                                to={`/wiki/entry/${entry.slug || entry.id}`}
                                style={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                <div className="case-item" style={{ cursor: 'pointer' }}>
                                    <div className="case-info">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                                            <h3 style={{ margin: 0, fontSize: '0.95rem' }}>
                                                {entry.title_en || entry.title || 'Untitled'}
                                            </h3>
                                            {entry.status && <StatusBadge status={entry.status} />}
                                        </div>
                                        <p style={{ margin: 0 }}>
                                            {entry.category && typeof entry.category === 'object'
                                                ? entry.category.name
                                                : (entry.category || 'Uncategorized')} — {entry.vendor_name || 'Unknown vendor'}
                                        </p>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                        {entry.updated_at
                                            ? formatDistanceToNow(new Date(entry.updated_at), { addSuffix: true })
                                            : ''}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}